import { MFieldType } from '../types/MFieldType';
import { MFieldName } from '../types/MandatoryFields';
import { underscoreToCamelCase } from './underscoreToCamelCase';
import { IMandatoryField } from '@wix/cashier-core/dist/src/services/MandatoryFields/types';

export interface UIMFieldMeta {
  type: MFieldType;
  name: MFieldName;
  label: string;
  labelForCountry?: {
    [countryCode: string]: string;
  };
  required?: boolean;
  editable?: boolean;
  errorMessage: string;
  errorMessageForCountry?: {
    [countryCode: string]: string;
  };
  placeholder?: string;
  placeholderForCountry?: {
    [countryCode: string]: string;
  };
  priority: number;
  column?: 'left' | 'right';
  values?: {
    id?: string;
    name?: string;
  }[];
}

export const mandatoryFieldsMeta: Record<MFieldName, UIMFieldMeta> = {
  countryCode: {
    type: MFieldType.CountryCode,
    name: 'countryCode',
    label: 'countryCode.input.label',
    required: true,
    editable: true,
    errorMessage: 'countryCode.input.error',
    priority: 40,
  },
  email: {
    type: MFieldType.Email,
    name: 'email',
    label: 'email.input.label',
    required: true,
    editable: true,
    errorMessage: 'email.input.error',
    priority: 90,
  },
  firstName: {
    type: MFieldType.Text,
    name: 'firstName',
    label: 'firstName.input.label',
    required: true,
    editable: true,
    errorMessage: 'firstName.input.error',
    priority: 80,
  },
  lastName: {
    type: MFieldType.Text,
    name: 'lastName',
    label: 'lastName.input.label',
    required: true,
    editable: true,
    errorMessage: 'lastName.input.error',
    priority: 70,
  },
  address: {
    type: MFieldType.Text,
    name: 'address',
    label: 'address.input.label',
    required: true,
    editable: true,
    errorMessage: 'address.input.error',
    priority: 60,
  },
  city: {
    type: MFieldType.Text,
    name: 'city',
    label: 'city.input.label',
    required: true,
    editable: true,
    errorMessage: 'city.input.error',
    priority: 50,
  },
  state: {
    type: MFieldType.State,
    name: 'state',
    label: 'state.input.label',
    required: true,
    editable: true,
    errorMessage: 'state.input.error',
    priority: 30,
  },
  zipCode: {
    type: MFieldType.ZipCode,
    name: 'zipCode',
    label: 'zipCode.input.label',
    required: true,
    editable: true,
    errorMessage: 'zipCode.input.error',
    priority: 20,
  },
  phone: {
    type: MFieldType.PhoneTel,
    name: 'phone',
    label: 'phone.input.label',
    required: true,
    editable: true,
    errorMessage: 'phone.input.error',
    priority: 10,
  },
  fake: {
    type: MFieldType.Text,
    name: 'fake',
    label: 'fake.input.label',
    required: true,
    editable: true,
    errorMessage: 'fake.input.error',
    priority: 250,
  },
  holderId: {
    type: MFieldType.Text,
    name: 'holderId',
    label: 'holderId.input.label',
    labelForCountry: {
      arg: 'holderId.input.label.arg',
      bra: 'holderId.input.label.bra',
      chl: 'holderId.input.label.chl',
      col: 'holderId.input.label.col',
      mex: 'holderId.input.label.mex',
      per: 'holderId.input.label.per',
      tur: 'holderId.input.label.tur',
      ury: 'holderId.input.label.ury',
      isr: 'holderId.input.label.isr',
    },
    required: true,
    editable: true,
    errorMessage: 'holderId.input.error',
    errorMessageForCountry: {
      arg: 'holderId.input.error.arg',
      bra: 'holderId.input.error.bra',
      chl: 'holderId.input.error.chl',
      col: 'holderId.input.error.col',
      mex: 'holderId.input.error.mex',
      per: 'holderId.input.error.per',
      tur: 'holderId.input.error.tur',
      ury: 'holderId.input.error.ury',
      isr: 'holderId.input.error.isr',
    },
    priority: 150,
  },
  street: {
    type: MFieldType.Text,
    name: 'street',
    label: 'street.input.label',
    required: true,
    editable: true,
    errorMessage: 'street.input.error',
    priority: 65,
  },
  houseNumber: {
    type: MFieldType.Text,
    name: 'houseNumber',
    label: 'houseNumber.input.label',
    required: true,
    editable: true,
    errorMessage: 'houseNumber.input.error',
    priority: 65,
  },
  issuerBank: {
    type: MFieldType.IssuerBank,
    name: 'issuerBank',
    label: 'issuerBank.input.label',
    placeholder: 'issuerBank.input.placeholder',
    required: true,
    editable: false,
    errorMessage: 'issuerBank.input.error',
    priority: 160,
  },
};

type MFieldCountryCode = 'jpn' | 'deu' | 'kor' | 'aus' | 'bra';

export const MANDATORY_FIELDS_ORDER: {
  [countryCode in MFieldCountryCode | 'default']: MFieldName[];
} = {
  default: [
    'fake',
    'issuerBank',
    'holderId',
    'email',
    'firstName',
    'lastName',
    'street',
    'houseNumber',
    'address',
    'city',
    'countryCode',
    'state',
    'zipCode',
    'phone',
  ],
  jpn: [
    'fake',
    'issuerBank',
    'holderId',
    'email',
    'lastName',
    'firstName',
    'countryCode',
    'zipCode',
    'state',
    'city',
    'address',
    'street',
    'houseNumber',
    'phone',
  ],
  deu: [
    'fake',
    'issuerBank',
    'holderId',
    'firstName',
    'lastName',
    'email',
    'street',
    'houseNumber',
    'address',
    'zipCode',
    'city',
    'countryCode',
    'state',
    'phone',
  ],
  kor: [
    'fake',
    'issuerBank',
    'holderId',
    'email',
    'lastName',
    'firstName',
    'street',
    'houseNumber',
    'countryCode',
    'zipCode',
    'state',
    'city',
    'address',
    'phone',
  ],
  aus: [
    'fake',
    'issuerBank',
    'holderId',
    'firstName',
    'lastName',
    'email',
    'street',
    'houseNumber',
    'address',
    'city',
    'countryCode',
    'state',
    'zipCode',
    'phone',
  ],
  bra: [
    'fake',
    'issuerBank',
    'countryCode',
    'email',
    'firstName',
    'lastName',
    'holderId', // 'CPF/CNPJ'
    'zipCode',
    'address',
    'city',
    'state',
    'street',
    'houseNumber',
    'phone',
  ],
};

export const sortMFieldsByCountryFactory = (countryCode?: string) => {
  const mfOrder =
    MANDATORY_FIELDS_ORDER[countryCode?.toLowerCase()] ||
    MANDATORY_FIELDS_ORDER.default;

  return (mf1: MFieldName, mf2: MFieldName): number => {
    const mf1Index = mfOrder.indexOf(mf1);

    if (mf1Index === -1) {
      return 1;
    }

    const mf2Index = mfOrder.indexOf(mf2);

    if (mf2Index === -1) {
      return -1;
    }

    return mf1Index - mf2Index;
  };
};

export const getMFieldShortName = (rawFieldName: string): MFieldName => {
  // Mapping to fieldName and its last part after the dot: 'billing.info.address' => 'address'
  // Note! Exception - 'description.billingAddress.taxIdentifier.id' => 'holderId' - will be
  // removed when server switches from using 'holder_id' to 'taxIdentifier'
  // TODO: Revise splitting by '.'

  if (rawFieldName === 'description.billingAddress.taxIdentifier.id') {
    return 'holderId';
  }

  return rawFieldName.split('.').pop() as MFieldName;
};

export const mFieldGroups: [MFieldName, MFieldName][] = [
  ['countryCode', 'state'],
  ['zipCode', 'phone'],
  ['street', 'houseNumber'],
];

const transformToUIMField = (mField: IMandatoryField): UIMFieldMeta => {
  const mFieldNameUI: string = underscoreToCamelCase(
    getMFieldShortName(mField.fieldName)
  );

  if (!mandatoryFieldsMeta.hasOwnProperty(mFieldNameUI)) {
    return null;
  }

  return {
    ...mandatoryFieldsMeta[mFieldNameUI],
    ...(mField.values && { values: mField.values }),
  };
};

export const transformToUIMFields = (
  mFields: IMandatoryField[]
): UIMFieldMeta[] =>
  mFields.map(transformToUIMField).filter((mField) => mField !== null);

export const isMFieldValueExists = (value: string): boolean =>
  value !== null && value !== undefined && value !== '';

export const applyColumnsToMFields = (
  mFields: UIMFieldMeta[]
): UIMFieldMeta[] => {
  if (mFields.length <= 2) {
    return mFields;
  }

  const resultMFields: UIMFieldMeta[] = [...mFields];

  for (const [leftMFieldName, rightMFieldName] of mFieldGroups) {
    const leftMFieldI = resultMFields.findIndex(
      ({ name }) => name === leftMFieldName
    );
    const rightMFieldI = resultMFields.findIndex(
      ({ name }) => name === rightMFieldName
    );

    if (
      leftMFieldI !== -1 &&
      rightMFieldI !== -1 &&
      rightMFieldI === leftMFieldI + 1
    ) {
      resultMFields[leftMFieldI] = {
        ...resultMFields[leftMFieldI],
        column: 'left',
      };
      resultMFields[rightMFieldI] = {
        ...resultMFields[rightMFieldI],
        column: 'right',
      };
    }
  }

  return resultMFields;
};

export const allPossibleMFieldsNames: MFieldName[] = [
  'issuerBank',
  'holderId',
  'email',
  'firstName',
  'lastName',
  'street',
  'houseNumber',
  'address',
  'city',
  'countryCode',
  'state',
  'zipCode',
  'phone',
];
